import React from 'react'
import get from 'lodash.get'

import logger from '../logger'
import storageAvailable from '../analytics/storage'

// Load from session storage on parent page
// to avoid 3rd party blocking / safari etc.
// this is accessed via window.xprops.sessionState

interface SessionStatePayload {
  countryCode: string
  name: string
  email: string
  taxInfo?: TaxInfo
  bic: string
  bankId: string
  processingInstruction: string
  metadata: {
    fieldsSessionID: string
  }
  addressLine1: string
  city: string
  state: string
  postalCode: string
}

interface BillingAddress {
  address_line_1: string | null
  admin_area_2: string | null
  admin_area_1: string | null
  postal_code: string | null
}

interface TaxInfo {
  tax_id: string
  tax_id_type: string
}

type StorageResponse = Promise<{
  country: string
  name: string
  email: string
  taxInfo?: TaxInfo | null
  bic: string | null
  bankId: string | null
  billingAddress: BillingAddress | null
  processingInstruction: string | null
  metadata: object | null
}>

export function useSessionState() {
  const [hasLoaded, setHasLoaded] = React.useState(false)

  const sessionState = get(window, 'xprops.sessionState')
  if (!sessionState) {
    logger.error('no_sessionState', {
      err: 'no xprops.sessionState found',
    })
    throw new Error('no sessionState')
  }

  React.useEffect(() => {
    // log availability of window storage
    if (
      !storageAvailable('localStorage') ||
      !storageAvailable('sessionStorage')
    ) {
      logger.error('window_storage_unavailabile', {
        err: `localStorage: ${storageAvailable(
          'localStorage',
        )}, sessionStorage: ${storageAvailable('sessionStorage')}`,
        userAgent: window.navigator.userAgent,
      })
    }
  }, [])

  async function load(fundingSource: string): StorageResponse {
    const cachedConfirmPayload = await sessionState.get(
      `__confirm_${fundingSource}_payload__`,
    )

    const { country_code, name, email, tax_info, bic, bank_id, metadata } = get(
      cachedConfirmPayload,
      `payment_source[${fundingSource}]`,
      {
        country_code: null,
        name: '',
        email: '',
        tax_info: null,
        bic: null,
        bank_id: null,
        metadata: null,
      },
    )

    const billingAddress = get(
      cachedConfirmPayload,
      `payment_source[${fundingSource}][billing_address]`,
      {
        address_line_1: '',
        admin_area_2: '',
        admin_area_1: '',
        postal_code: '',
      },
    )

    const processingInstruction = get(
      cachedConfirmPayload,
      ['processing_instruction'],
      null,
    )

    logger.info('session_storage_loaded', {
      fundingSource,
      country: country_code,
      name,
      email,
      taxInfo: tax_info,
      bic,
      bankId: bank_id,
      billingAddress,
      processingInstruction,
    })

    setHasLoaded(true)

    return {
      country: country_code,
      name,
      email,
      taxInfo: tax_info,
      bic,
      bankId: bank_id,
      billingAddress,
      processingInstruction,
      metadata,
    }
  }

  async function save(fundingSource: string, payload: SessionStatePayload) {
    // if we have not loaded the state do not update
    if (!hasLoaded) {
      return
    }

    // TODO: Check if Logger interface is expecting metadata key to have object value
    logger.info('session_storage_updated', {
      fundingSource,
      ...payload,
    })

    await sessionState.set(`__confirm_${fundingSource}_payload__`, {
      payment_source: {
        [fundingSource]: {
          country_code: payload.countryCode,
          name: payload.name,
          email: payload.email,
          tax_info: payload.taxInfo,
          bic: payload.bic,
          bank_id: payload.bankId,
          metadata: payload.metadata,
          billing_address: {
            address_line_1: payload.addressLine1,
            admin_area_2: payload.city,
            admin_area_1: payload.state,
            postal_code: payload.postalCode,
          },
        },
      },
      processing_instruction: payload.processingInstruction,
    })
  }

  return {
    hasLoaded,
    load,
    save,
  }
}
